import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, Sparkles, Calendar, MapPin, Users, TrendingUp, Award, Heart, Star, Play, ChevronLeft, ChevronRight, Sprout, Cpu, Stethoscope, GraduationCap, ShoppingBag, Landmark, Scissors, Utensils, Factory, PenTool } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1770627016447-cb9d29ed0398?crop=entropy&cs=srgb&fm=jpg&w=1800&q=85";
const NETWORK = "https://images.unsplash.com/photo-1590650046871-92c887180603?crop=entropy&cs=srgb&fm=jpg&w=1800&q=85";
const SPEAKING = "https://images.unsplash.com/photo-1551731409-43eb3e517a1a?crop=entropy&cs=srgb&fm=jpg&w=1800&q=85";
const ABOUT = "https://images.unsplash.com/photo-1711182673833-7e11dffa0eec?crop=entropy&cs=srgb&fm=jpg&w=1800&q=85";
const TESTI_IMG = "https://images.unsplash.com/photo-1763475945531-7241e0015363?crop=entropy&cs=srgb&fm=jpg&w=800&q=85";

const SLIDES = [
  {
    tag: "Powerful Women's Network",
    title: "Where women",
    titleItalic: "grow business.",
    desc: "A serious platform for founders, professionals and leaders to unlock referrals, mentorship and revenue with the women who get you.",
    image: NETWORK,
    cta: { label: "Join WOWSHE", to: "/membership" },
    accent: "champagne",
    stat: { value: "₹4.2Cr", label: "Referrals exchanged · 2026" },
  },
  {
    tag: "Sisterhood",
    title: "Ambition,",
    titleItalic: "in company.",
    desc: "Trusted friendships forged over chapter dinners, mastermind circles and honest conversations. Never ambitious alone again.",
    image: NETWORK,
    cta: { label: "Meet the Community", to: "/directory" },
    accent: "rose",
    stat: { value: "2,400+", label: "Members across India" },
  },
  {
    tag: "CSR · Women & Girls",
    title: "Lift as we",
    titleItalic: "rise.",
    desc: "Every WOWSHE membership funds skilling, scholarships and safety initiatives for under-served women and girls.",
    image: NETWORK,
    cta: { label: "Our CSR Work", to: "/about" },
    accent: "champagne",
    stat: { value: "500+", label: "Girls & Women · Target 2026" },
  },
];

export default function Home() {
  const [stats, setStats] = useState({ members: 2400, chapters: 28, events: 180, cities: 35, referrals_value: "₹4.2Cr" });
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [stories, setStories] = useState([]);
  const [email, setEmail] = useState("");
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/events", { params: { upcoming: true, limit: 3 } }).then((r) => setEvents(r.data)).catch(() => {});
    api.get("/members", { params: { limit: 6 } }).then((r) => setMembers(r.data)).catch(() => {});
    api.get("/stories").then((r) => setStories(r.data)).catch(() => {});
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 5500);
    return () => clearInterval(t);
  }, [paused]);

  const next = useCallback(() => setSlide((s) => (s + 1) % SLIDES.length), []);
  const prev = useCallback(() => setSlide((s) => (s - 1 + SLIDES.length) % SLIDES.length), []);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Welcome aboard.");
      setEmail("");
    } catch (err) {
      toast.error("Please try again.");
    }
  };

  const active = SLIDES[slide];

  return (
    <div data-testid="home-page">
      {/* HERO — Sliding Carousel */}
      <section
        data-testid="hero-carousel"
        className="relative h-[78vh] min-h-[560px] md:min-h-[640px] overflow-hidden bg-aubergine"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Slide images — crossfade */}
        {SLIDES.map((s, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-[1200ms] ease-out"
            style={{ opacity: i === slide ? 1 : 0 }}
            aria-hidden={i !== slide}
          >
            <img
              src={s.image}
              alt=""
              className="w-full h-full object-cover scale-105 transition-transform duration-[8000ms] ease-out"
              style={{ transform: i === slide ? "scale(1.08)" : "scale(1.0)" }}
            />
            {/* Gradient overlays for legibility */}
            <div className="absolute inset-0 bg-gradient-to-r from-aubergine via-aubergine/85 to-aubergine/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-aubergine via-aubergine/30 to-aubergine/40" />
          </div>
        ))}

        {/* Grain */}
        <div className="absolute inset-0 grain opacity-20 pointer-events-none" />

        {/* Top issue strip */}
        <div className="relative border-b border-ivory/15 z-20">
          <div className="container-luxe py-3 flex items-center justify-between gap-3 text-[10px] tracking-[0.3em] uppercase text-ivory/70 font-medium">
            <span className="flex items-center gap-2 shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-rose animate-pulse" />
              <span className="hidden sm:inline">Founding Cohort · </span>Hyderabad 2026
            </span>
            <span className="hidden lg:block truncate">Hyderabad <span className="text-champagne">· Now</span> · Bengaluru · Chennai · Mumbai · Delhi <span className="text-ivory/40">· Coming Soon</span></span>
            <span className="text-champagne shrink-0">Applications Open</span>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="container-luxe relative h-[calc(100%-3rem)] z-20 flex flex-col justify-center py-8 md:py-12">
          {/* Center: copy + featured panel */}
          <div className="flex-1 grid grid-cols-12 gap-8 items-center">
            {/* LEFT — copy (re-keyed per slide for staggered animation) */}
            <div key={slide} className="col-span-12 md:col-span-7 animate-fade-up">
              {/* Slide tag */}
              <div className="inline-flex items-center gap-3 mb-5 px-4 py-1.5 rounded-full bg-ivory/10 backdrop-blur border border-ivory/15">
                <span className={`w-1.5 h-1.5 rounded-full bg-${active.accent}`} />
                <span className="text-[10px] tracking-[0.3em] uppercase text-ivory/80 font-medium">{active.tag}</span>
                {SLIDES.length > 1 && (
                  <>
                    <span className="text-ivory/30">·</span>
                    <span className="text-[10px] tracking-[0.25em] uppercase text-ivory/60">{String(slide + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}</span>
                  </>
                )}
              </div>

              {/* Headline */}
              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[5rem] xl:text-[6rem] text-ivory leading-[0.95] tracking-[-0.015em] max-w-2xl">
                <span className="block">{active.title}</span>
                <span className="block">
                  <em
                    className="font-normal italic"
                    style={{
                      fontFamily: "Playfair Display",
                      background:
                        active.accent === "rose"
                          ? "linear-gradient(120deg, #C04085 0%, #D9A55C 100%)"
                          : active.accent === "teal"
                          ? "linear-gradient(120deg, #1F8E97 0%, #D9A55C 100%)"
                          : active.accent === "plum"
                          ? "linear-gradient(120deg, #C04085 0%, #1F8E97 100%)"
                          : "linear-gradient(120deg, #D9A55C 0%, #C04085 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    {active.titleItalic}
                  </em>
                </span>
              </h1>

              <p className="mt-6 text-lg md:text-xl text-ivory/75 max-w-xl leading-relaxed font-light line-clamp-2">
                {active.desc}
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-5">
                <Link to={active.cta.to} data-testid="hero-primary-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-ivory text-aubergine px-7 py-3.5 text-sm font-medium tracking-wide transition-all duration-300 hover:bg-champagne hover:-translate-y-0.5 hover:shadow-luxeHover group">
                  {active.cta.label}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link to="/about" data-testid="hero-watch-cta" className="inline-flex items-center gap-3 text-ivory group">
                  <span className="w-12 h-12 rounded-full border border-ivory/30 flex items-center justify-center transition-all group-hover:bg-ivory group-hover:text-aubergine group-hover:scale-110">
                    <Play className="w-4 h-4 ml-0.5 fill-current" />
                  </span>
                  <span className="text-sm tracking-wide link-underline">Watch our story · 2 min</span>
                </Link>
              </div>

              {/* Social proof */}
              <div className="mt-7 flex flex-wrap items-center gap-6">
                <div className="flex -space-x-3">
                  {[
                    "https://images.unsplash.com/photo-1770627016447-cb9d29ed0398?w=120&h=120&fit=crop",
                    "https://images.unsplash.com/photo-1711182673833-7e11dffa0eec?w=120&h=120&fit=crop",
                    "https://images.unsplash.com/photo-1551731409-43eb3e517a1a?w=120&h=120&fit=crop",
                    "https://images.unsplash.com/photo-1763475945531-7241e0015363?w=120&h=120&fit=crop",
                  ].map((src, i) => (
                    <img key={i} src={src} alt="" className="w-10 h-10 rounded-full border-2 border-aubergine object-cover" />
                  ))}
                  <div className="w-10 h-10 rounded-full border-2 border-aubergine bg-gradient-to-br from-rose to-plum text-ivory flex items-center justify-center text-[10px] font-medium">
                    +2.4k
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-0.5 text-champagne">
                    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-champagne" />)}
                    <span className="ml-2 text-xs text-ivory font-medium">4.9 / 5</span>
                  </div>
                  <p className="text-xs text-ivory/55 mt-0.5 font-light">1,800+ verified member reviews</p>
                </div>
              </div>
            </div>

            {/* RIGHT — Slide stat card (visible from md tablet up) */}
            <div className="hidden md:flex col-span-12 md:col-span-5 justify-end mt-6 md:mt-0">
              <div key={slide + "-stat"} className="animate-fade-up relative">
                <div className="w-64 md:w-72 lg:w-80 bg-gradient-to-br from-ivory/20 to-ivory/5 backdrop-blur-2xl border border-ivory/25 rounded-3xl p-6 md:p-7 lg:p-8 shadow-luxeHover">
                  <div className="flex items-center justify-between mb-5 lg:mb-6">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-champagne font-medium">Featured Now</span>
                    <span className={`w-2 h-2 rounded-full bg-${active.accent} animate-pulse`} />
                  </div>
                  <p className="font-display text-5xl lg:text-6xl text-ivory leading-none drop-shadow-lg">{active.stat.value}</p>
                  <p className="mt-4 text-xs tracking-[0.25em] uppercase text-ivory/80">{active.stat.label}</p>
                  <div className="mt-7 lg:mt-8 pt-5 lg:pt-6 border-t border-ivory/20 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-3.5 h-3.5 text-champagne" />
                      <span className="text-xs text-ivory/80">Growing weekly</span>
                    </div>
                    <Link to={active.cta.to} className="text-xs text-champagne tracking-wide link-underline font-medium">
                      Explore →
                    </Link>
                  </div>
                </div>
                {/* Floating decorative elements */}
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full border border-champagne/40 animate-spin-slow" />
                <Sparkles className="absolute -bottom-3 -left-3 w-5 h-5 text-champagne animate-float" />
              </div>
            </div>
          </div>

          {/* BOTTOM — Carousel controls (only shown when multiple slides) */}
          {SLIDES.length > 1 && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-6 border-t border-ivory/15">
            {/* Slide title pills (clickable) */}
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              {SLIDES.map((s, i) => (
                <button
                  key={i}
                  data-testid={`hero-slide-${i}`}
                  onClick={() => setSlide(i)}
                  className={`group flex items-center gap-2 transition-all ${i === slide ? "" : "opacity-50 hover:opacity-100"}`}
                >
                  <span className={`text-[10px] tracking-widest font-medium ${i === slide ? "text-champagne" : "text-ivory/60"}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className={`text-xs tracking-wide ${i === slide ? "text-ivory" : "text-ivory/60"} hidden md:inline`}>
                    {s.tag}
                  </span>
                  {/* Progress bar for active */}
                  <span className="relative w-12 md:w-20 h-px bg-ivory/20 overflow-hidden">
                    {i === slide && (
                      <span
                        key={slide + (paused ? "-p" : "-r")}
                        className="absolute inset-y-0 left-0 bg-champagne"
                        style={{
                          width: "100%",
                          animation: paused ? "none" : "slideProgress 5.5s linear forwards",
                        }}
                      />
                    )}
                  </span>
                </button>
              ))}
            </div>

            {/* Arrow controls */}
            <div className="flex items-center gap-3">
              <button data-testid="hero-prev" onClick={prev} aria-label="Previous slide" className="w-11 h-11 rounded-full border border-ivory/25 text-ivory flex items-center justify-center hover:bg-ivory hover:text-aubergine transition-all">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button data-testid="hero-next" onClick={next} aria-label="Next slide" className="w-11 h-11 rounded-full border border-ivory/25 text-ivory flex items-center justify-center hover:bg-ivory hover:text-aubergine transition-all">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Stat strip below hero */}
      <section className="relative border-b border-aubergine/10 bg-white">
        <div className="container-luxe py-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x md:divide-aubergine/10">
          {[
            { v: "120+", l: "Founding Applications" },
            { v: "36", l: "Workshops · 2026" },
            { v: "12", l: "Wellness Programs" },
            { v: "500+", l: "Girls & Women Impacted" },
          ].map((s) => (
            <div key={s.l} className="md:px-8 first:md:pl-0 last:md:pr-0">
              <p className="font-display text-4xl text-aubergine">{s.v}</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-plum mt-2">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* City marquee — Hyderabad live, others coming soon */}
      <section className="relative border-b border-aubergine/10 bg-lavender/40 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-5">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-10 px-6 shrink-0">
              <span className="flex items-center gap-3 text-xs tracking-[0.3em] uppercase font-semibold text-rose">
                <span className="w-2 h-2 rounded-full bg-rose animate-pulse" />
                Hyderabad · Now
              </span>
              {["Bengaluru", "Chennai", "Mumbai", "Delhi NCR", "Pune", "Kolkata", "Ahmedabad", "Jaipur", "Kochi", "Chandigarh"].map((c) => (
                <span key={c + k} className="flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-plumSoft/70 font-medium">
                  <span className="w-1 h-1 rounded-full bg-champagne" />
                  {c} <span className="text-plumSoft/40">· Soon</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* THE STORY WE'RE REWRITING — Women × Indian GDP */}
      <section className="section-pad container-luxe relative overflow-hidden">
        {/* Ambient decor */}
        <div aria-hidden className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-rose/[0.08] blur-3xl pointer-events-none" />
        <div aria-hidden className="absolute -bottom-32 -left-24 w-[380px] h-[380px] rounded-full bg-champagne/[0.10] blur-3xl pointer-events-none" />

        <div className="relative rounded-3xl overflow-hidden mb-14 border border-aubergine/10 bg-gradient-to-br from-white via-lavender/40 to-champagne/[0.08] shadow-luxe">
          {/* Ornamental accents */}
          <div aria-hidden className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-rose/25 to-transparent rounded-bl-full pointer-events-none" />
          <div aria-hidden className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-champagne/[0.14] blur-2xl pointer-events-none" />
          <div aria-hidden className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-rose via-champagne to-rose opacity-70" />
          <div aria-hidden className="absolute inset-y-6 left-6 w-1 rounded-full bg-gradient-to-b from-rose via-champagne to-rose opacity-60 hidden lg:block" />

          <div className="relative grid lg:grid-cols-12 gap-10 p-8 md:p-12 lg:p-16">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose/10 border border-rose/20">
                <span className="w-1.5 h-1.5 rounded-full bg-rose animate-pulse" />
                <span className="text-[10px] tracking-[0.28em] uppercase text-rose font-medium">The story we&rsquo;re rewriting</span>
              </span>
              <h2 className="section-title mt-5">
                Women hold up half the sky.<br />
                <span className="text-champagne italic">And barely one-fifth of India&rsquo;s GDP.</span>
              </h2>

              {/* Highlight number chips */}
              <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
                <StatChip value="18%" label="of GDP from women" tone="rose" />
                <StatChip value="432M" label="working-age women" tone="aubergine" />
                <StatChip value="₹64L Cr" label="opportunity · 2030" tone="champagne" />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-5 text-plumSoft/90 text-[15px] leading-relaxed">
              <p>
                India&rsquo;s ₹300 lakh crore economy is powered largely by men. Women contribute only around <b className="text-aubergine">18% of GDP</b> — one of the lowest ratios in the world, and less than half the global average of 37%.
              </p>
              <p>
                Yet <b className="text-aubergine">432 million</b> women of working age live in this country. If we simply closed the gender participation gap, McKinsey estimates India could add <b className="text-aubergine">₹64 lakh crore</b> (US $770B) to its GDP by 2030 — the largest opportunity of any economy on earth.
              </p>
              <blockquote className="mt-6 pl-5 border-l-2 border-champagne">
                <p className="font-display text-lg md:text-xl text-aubergine italic leading-snug">
                  &ldquo;WOWSHE exists because that gap doesn&rsquo;t close by policy alone. It closes when women help other women win — one referral, one deal, one bold ask at a time.&rdquo;
                </p>
              </blockquote>
            </div>
          </div>
        </div>

        {/* Sector grid — hover / tap reveals */}
        <div className="mb-6 flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <p className="overline text-rose">Where we already show up</p>
            <h3 className="font-display text-3xl md:text-4xl text-aubergine mt-2 leading-tight">Sector-wise women in India&rsquo;s workforce</h3>
          </div>
          <p className="text-xs text-plumSoft/70 italic max-w-sm">
            Hover a card to see share of women &amp; that sector&rsquo;s contribution to India&rsquo;s GDP.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {SECTORS.map((s) => (
            <SectorCard key={s.name} sector={s} />
          ))}
        </div>

        {/* Footnote + closing CTA */}
        <div className="mt-10 flex flex-wrap items-center justify-between gap-6 pt-6 border-t border-aubergine/10">
          <p className="text-[10px] text-plumSoft/60 tracking-[0.15em] uppercase">
            Sources · Periodic Labour Force Survey (PLFS 2022-23) · McKinsey Global Institute · World Bank India
          </p>
          <Link
            to="/apply"
            data-testid="story-cta-apply"
            className="inline-flex items-center gap-2 bg-aubergine text-ivory px-6 py-3 rounded-full text-[12px] tracking-[0.14em] uppercase hover:bg-aubergine/90 transition-colors"
          >
            Be part of the rewrite <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* THREE PILLARS — Empowerment · Wellness · CSR */}
      <section id="why" className="section-pad container-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-5">
            <p className="overline">Why WOWSHE</p>
            <h2 className="font-display text-4xl md:text-5xl text-aubergine mt-4 leading-tight">
              Three pillars. <em className="not-italic text-rose">One purpose.</em>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-lg text-plumSoft leading-relaxed font-light">
              WOWSHE is built around three commitments — practical empowerment through
              workshops, full-spectrum health & wellness, and meaningful CSR action for
              women and girls who don't yet have access to either.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Pillar 1 — Empowerment Workshops */}
          <article className="card-luxe overflow-hidden group">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={SPEAKING} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-7">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rose/10 text-rose flex items-center justify-center">
                  <Award className="w-5 h-5" />
                </div>
                <span className="overline">Pillar 01</span>
              </div>
              <h3 className="font-display text-2xl text-aubergine mt-5">Empowerment Workshops</h3>
              <p className="mt-3 text-plumSoft font-light leading-relaxed">
                Practical, high-impact trainings on negotiation, leadership, financial literacy,
                personal branding and fundraising — taught by women who have done it.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-aubergine">
                <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-rose" /> 36 workshops planned in 2026</li>
                <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-rose" /> Cohort-based · live in Hyderabad + online</li>
              </ul>
            </div>
          </article>

          {/* Pillar 2 — Health & Wellness */}
          <article className="card-luxe overflow-hidden group">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={ABOUT}
                alt=""
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="p-7">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal/10 text-teal flex items-center justify-center">
                  <Heart className="w-5 h-5" />
                </div>
                <span className="overline">Pillar 02</span>
              </div>
              <h3 className="font-display text-2xl text-aubergine mt-5">Health & Wellness</h3>
              <p className="mt-3 text-plumSoft font-light leading-relaxed">
                Wellness retreats, fertility & menopause panels, mental-health circles,
                fitness pods and nutrition guidance — designed around the realities of Indian women.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-aubergine">
                <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-teal" /> 12 programs across the year</li>
                <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-teal" /> Specialist-led · members only</li>
              </ul>
            </div>
          </article>

          {/* Pillar 3 — CSR */}
          <article className="card-luxe overflow-hidden group">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={NETWORK} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-7">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-champagne/20 text-champagne flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>
                <span className="overline">Pillar 03</span>
              </div>
              <h3 className="font-display text-2xl text-aubergine mt-5">CSR · Women & Girls</h3>
              <p className="mt-3 text-plumSoft font-light leading-relaxed">
                Every WOWSHE membership funds skilling, scholarships and safety initiatives
                for under-served girls and women across Telangana — and soon, across India.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-aubergine">
                <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-champagne" /> 500+ girls & women · 2026 target</li>
                <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-champagne" /> Skilling · scholarships · safety</li>
              </ul>
            </div>
          </article>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="section-pad bg-lavender/60">
        <div className="container-luxe">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
            <div>
              <p className="overline">Upcoming</p>
              <h2 className="font-display text-4xl md:text-5xl text-aubergine mt-3">Events & Workshops</h2>
            </div>
            <Link to="/events" data-testid="home-events-link" className="text-sm text-plum flex items-center gap-2 hover:gap-3 transition-all">
              View calendar <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(events.length ? events : [1, 2, 3]).map((ev, i) =>
              ev.id ? (
                <article key={ev.id} className="card-luxe overflow-hidden group">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={ev.image} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <span className="overline">{ev.type}</span>
                    <h3 className="font-display text-2xl text-aubergine mt-2 leading-snug">{ev.title}</h3>
                    <div className="mt-4 text-sm text-plumSoft space-y-1.5">
                      <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>{new Date(ev.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span></div>
                      <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /><span>{ev.location}</span></div>
                    </div>
                    <Link to="/events" className="mt-5 inline-block text-sm text-plum link-underline">RSVP →</Link>
                  </div>
                </article>
              ) : (
                <div key={i} className="card-luxe p-6 h-80 animate-pulse bg-white/60" />
              )
            )}
          </div>
        </div>
      </section>

      {/* FOUNDING COHORT */}
      <section className="section-pad container-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          <div className="lg:col-span-7">
            <p className="overline">The Founding Cohort</p>
            <h2 className="font-display text-4xl md:text-5xl text-aubergine mt-3 leading-tight">
              The first <em className="not-italic text-rose">120 women</em> shaping WOWSHE Hyderabad.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-end">
            <Link to="/membership" data-testid="home-directory-link" className="text-sm text-plum link-underline">Apply to join →</Link>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {members.slice(0, 6).map((m) => (
            <article key={m.id} className="card-luxe overflow-hidden group">
              <div className="aspect-square overflow-hidden">
                <img src={m.avatar || HERO} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-4">
                <h3 className="font-display text-base text-aubergine">{m.name}</h3>
                <p className="text-xs text-plumSoft mt-1">{m.business}</p>
                <p className="text-[10px] uppercase tracking-widest text-plum mt-2">{m.city}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* WHAT WE'RE BUILDING — dark band */}
      <section className="section-pad bg-aubergine text-ivory relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="container-luxe relative">
          <p className="overline !text-champagne">What we're building · 2026</p>
          <h2 className="font-display text-4xl md:text-5xl mt-4 max-w-3xl">
            A first-of-its-kind women's community in <em className="text-champagne not-italic">Hyderabad.</em>
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border-l border-champagne/40 pl-6">
              <p className="font-display text-5xl text-champagne">36</p>
              <h3 className="font-display text-2xl mt-4">Empowerment Workshops</h3>
              <p className="text-xs uppercase tracking-widest text-ivory/60 mt-1">Planned in 2026</p>
              <p className="text-ivory/80 text-sm mt-4 leading-relaxed font-light">
                Negotiation, leadership, fundraising, financial literacy, personal branding —
                taught by women who have built and led.
              </p>
            </div>
            <div className="border-l border-teal/60 pl-6">
              <p className="font-display text-5xl text-teal">12</p>
              <h3 className="font-display text-2xl mt-4">Health & Wellness Programs</h3>
              <p className="text-xs uppercase tracking-widest text-ivory/60 mt-1">Retreats · Panels · Pods</p>
              <p className="text-ivory/80 text-sm mt-4 leading-relaxed font-light">
                Fertility, menopause, mental health, fitness and nutrition —
                designed for the lived realities of Indian women.
              </p>
            </div>
            <div className="border-l border-rose/60 pl-6">
              <p className="font-display text-5xl text-rose">500+</p>
              <h3 className="font-display text-2xl mt-4">Girls & Women · CSR</h3>
              <p className="text-xs uppercase tracking-widest text-ivory/60 mt-1">Target Reach · 2026</p>
              <p className="text-ivory/80 text-sm mt-4 leading-relaxed font-light">
                Skilling, scholarships and safety programs for under-served girls and women
                across Telangana — funded by every membership.
              </p>
            </div>
          </div>
          <div className="mt-10">
            <Link to="/about" className="inline-flex items-center gap-2 text-champagne hover:gap-3 transition-all text-sm">
              How we'll get there <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUNDING MEMBER VOICE */}
      <section className="section-pad container-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img src={TESTI_IMG} alt="" className="rounded-2xl w-full h-[420px] object-cover" />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Quote className="w-12 h-12 text-rose mb-6" />
            <blockquote className="font-display text-3xl md:text-4xl text-aubergine leading-snug">
              "Hyderabad has incredible women — what it has not had, until now, is a room
              built only for them. WOWSHE is that room."
            </blockquote>
            <div className="mt-8">
              <p className="font-display text-lg text-aubergine">A note from our Founders</p>
              <p className="text-sm text-plumSoft">WOWSHE · Hyderabad · Founding Cohort 2026</p>
            </div>
          </div>
        </div>
      </section>

      {/* WORKSHOPS / Learning teaser */}
      <section className="section-pad bg-lavender/60">
        <div className="container-luxe grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <img src={SPEAKING} alt="" className="rounded-2xl w-full h-[440px] object-cover" />
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="overline">Workshops · Empowerment</p>
            <h2 className="font-display text-4xl md:text-5xl text-aubergine mt-3 leading-tight">
              Trainings that <em className="text-rose not-italic">change careers.</em>
            </h2>
            <p className="mt-6 text-plumSoft text-lg font-light leading-relaxed">
              Cohort-based programs taught by women who've built and led — paired with
              live workshops in Hyderabad on negotiation, leadership, financial literacy
              and personal branding.
            </p>
            <Link to="/learning" data-testid="home-learning-link" className="mt-8 btn-primary">
              Explore Workshops <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="section-pad container-luxe">
        <div className="bg-gradient-to-br from-aubergine to-plum text-ivory rounded-3xl p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-80 h-80 bg-champagne/10 rounded-full blur-3xl" />
          <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-rose/20 rounded-full blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <p className="overline !text-champagne">The WOWSHE Dispatch</p>
              <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
                A weekly letter. Six minutes. Zero fluff.
              </h2>
              <p className="mt-4 text-ivory/75 font-light">
                Curated reads on women in business, monthly chapter highlights, and the
                occasional unfiltered note from our founders.
              </p>
            </div>
            <form onSubmit={subscribe} className="md:col-span-5 flex flex-col gap-3">
              <input
                type="email"
                data-testid="newsletter-email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.name@company.in"
                className="w-full rounded-full bg-white/10 border border-ivory/20 backdrop-blur px-6 py-4 text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-champagne"
              />
              <button data-testid="newsletter-submit" type="submit" className="btn-gold w-full">
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="font-display text-4xl text-aubergine">{value}</p>
      <p className="text-xs tracking-[0.2em] uppercase text-plum mt-2">{label}</p>
    </div>
  );
}

function Benefit({ icon, title, desc, className = "", children }) {
  return (
    <div className={`card-luxe p-8 ${className}`}>
      <div className="w-12 h-12 rounded-full bg-lavender flex items-center justify-center text-plum">
        {icon}
      </div>
      <h3 className="font-display text-2xl text-aubergine mt-6">{title}</h3>
      <p className="mt-3 text-plumSoft leading-relaxed font-light">{desc}</p>
      {children}
    </div>
  );
}

/* ============================== SECTOR DATA ============================== */

const SECTORS = [
  { name: "Agriculture",       icon: Sprout,         women: "62%", gdp: "16%", note: "Largest employer of women — mostly informal." },
  { name: "Textiles & Apparel",icon: Scissors,       women: "60%", gdp: "2%",  note: "India's second-largest employer overall." },
  { name: "Beauty & Wellness", icon: PenTool,        women: "82%", gdp: "1.5%",note: "Female-founded, female-led — a rare parity sector." },
  { name: "Education",         icon: GraduationCap,  women: "50%", gdp: "4%",  note: "Half of India's teachers are women." },
  { name: "Healthcare",        icon: Stethoscope,    women: "30%", gdp: "2.4%",note: "Nurses & ASHA workers form the backbone." },
  { name: "IT & Services",     icon: Cpu,            women: "34%", gdp: "10%", note: "Highest paid sector for women — still not parity." },
  { name: "Banking & Finance", icon: Landmark,       women: "26%", gdp: "7%",  note: "Steady growth · policy tailwinds." },
  { name: "Retail & FMCG",     icon: ShoppingBag,    women: "20%", gdp: "10%", note: "D2C brands are shifting the balance." },
  { name: "Manufacturing",     icon: Factory,        women: "20%", gdp: "17%", note: "The largest GDP sector — smallest share of women." },
  { name: "Food & Hospitality",icon: Utensils,       women: "15%", gdp: "3%",  note: "Home-grown cloud kitchens changing the game." },
];

const STAT_CHIP_TONES = {
  rose:      { pill: "from-rose/15 to-rose/[0.06] border-rose/25",           value: "text-rose" },
  aubergine: { pill: "from-aubergine/12 to-aubergine/[0.05] border-aubergine/25", value: "text-aubergine" },
  champagne: { pill: "from-champagne/22 to-champagne/[0.08] border-champagne/40", value: "text-champagne" },
};

function StatChip({ value, label, tone = "rose" }) {
  const t = STAT_CHIP_TONES[tone] || STAT_CHIP_TONES.rose;
  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${t.pill} p-4 text-center`}>
      <p className={`font-display text-2xl md:text-[26px] leading-none ${t.value}`}>{value}</p>
      <p className="mt-2 text-[9.5px] tracking-[0.2em] uppercase text-plum leading-snug">{label}</p>
    </div>
  );
}

function SectorCard({ sector }) {
  const Icon = sector.icon;
  return (
    <div
      data-testid={`sector-${sector.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className="group relative rounded-2xl border border-aubergine/[0.08] bg-white overflow-hidden aspect-[4/5] cursor-pointer transition-shadow hover:shadow-luxe"
      tabIndex={0}
    >
      {/* Front — profession */}
      <div className="absolute inset-0 flex flex-col items-center justify-center p-5 text-center transition-all duration-500 group-hover:opacity-0 group-hover:-translate-y-1 group-focus-within:opacity-0 group-focus-within:-translate-y-1">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-aubergine to-plum flex items-center justify-center text-ivory mb-4 shadow-luxe">
          <Icon className="w-6 h-6" />
        </div>
        <p className="font-display text-lg text-aubergine leading-tight">{sector.name}</p>
        <p className="mt-1 text-[9px] tracking-[0.28em] uppercase text-champagne">Hover to reveal</p>
      </div>

      {/* Back — data reveal */}
      <div className="absolute inset-0 flex flex-col justify-between p-5 bg-gradient-to-br from-aubergine via-plum to-[#5E1380] text-ivory opacity-0 translate-y-3 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0">
        <div>
          <p className="text-[10px] tracking-[0.28em] uppercase text-champagne">{sector.name}</p>
          <div className="mt-4">
            <p className="text-[9px] tracking-[0.24em] uppercase text-ivory/70">Women in sector</p>
            <p className="font-display text-3xl text-ivory mt-1">{sector.women}</p>
          </div>
          <div className="mt-4">
            <p className="text-[9px] tracking-[0.24em] uppercase text-ivory/70">GDP contribution</p>
            <p className="font-display text-3xl text-champagne mt-1">{sector.gdp}</p>
          </div>
        </div>
        <p className="text-[11px] leading-relaxed text-ivory/85 italic">{sector.note}</p>
      </div>
    </div>
  );
}
